import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getLocale } from "react-i18nify";

import { Tooltip, ResponsiveContainer, PieChart, Pie, Sector } from "recharts";
import { Box, Divider, Grid, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import font from "src/styles/font";
import device from "src/styles/device";
import { Black, DarkGrey, Primary } from "src/styles/color";
import { convertIntToCurrency } from "src/common";
import { hexToRgba } from "src/common/color";

import LabelSection from "src/components/core/LabelSection";

const CategoryItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CategoryTitle = styled.div`
  width: 100%;
  display: flex;
  align-items: center;

  ::before {
    content: "";
    width: 4px;
    color: ${({ fill }) => fill || Primary};
    background: ${({ fill }) => fill || Primary};
    display: inline-block;
    height: 40px;
    margin-right: 15px;
    border-radius: 3px;
  }
`;

const CategoryValue = styled.div`
  font-size: ${font.size.lg};
  font-weight: 500;
  line-height: 24px;
  color: ${Black};

  @media ${device.tabletL} {
    font-size: ${font.size.md};
  }
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: 500;
  line-height: 24px;
  color: ${Black};
  text-align: left;

  display: flex;
  flex-direction: column;
`;

const SpanDesc = styled.span`
  font-size: ${font.size.xs};
  font-weight: 400;
  line-height: 1;
  color: ${DarkGrey};
  text-align: left;
`;

const chartColors = ["#E13B30", "#005FCC", "#E6B558", "#219653", "#A8A8A8"];

const renderActiveShape = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
  value,
}) => {
  return (
    <g>
      <text
        x={cx}
        y={cy - 16}
        dy={8}
        textAnchor="middle"
        fill={Black}
        style={{ fontSize: font.size.xl, fontWeight: 600 }}
      >
        {convertIntToCurrency(value, false)}
      </text>
      <text
        x={cx}
        y={cy + 8}
        dy={8}
        textAnchor="middle"
        fill={Black}
        style={{ fontSize: font.size.sm, fontWeight: 500 }}
      >
        {payload.itemCategory}
      </text>
      <text
        x={cx}
        y={cy + 32}
        dy={8}
        textAnchor="middle"
        fill={DarkGrey}
        style={{ fontSize: font.size.sm, fontWeight: 300 }}
      >
        {`(${((percent ?? 1) * 100).toFixed(2)}%)`}
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 5}
        outerRadius={(outerRadius ?? 0) + 10}
        fill={hexToRgba(fill, 0.7)}
      />
    </g>
  );
};

const PopularCategoryChart = ({
  data = [],
  selectedTimeRangeData,
  isAnimationActive = true,
  defaultIndex = undefined,
}) => {
  const isSmallerThanMobileL = useMediaQuery(device.mobileL);
  const isSmallerThanTabletL = useMediaQuery(device.tabletL);
  const isSmallerThanLaptop = useMediaQuery(device.laptop);
  const isSmallerThanDesktop = useMediaQuery(device.desktop);

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let formatData = data.slice(0, 4).map((item, index) => ({
      ...item,
      itemCategory: item["customEvent:item_type_th"],
      fill: chartColors[index % chartColors.length],
    }));
    let otherItems = data.slice(4);
    if (otherItems.length > 0) {
      formatData.push({
        itemCategory: getLocale() === "th" ? "อื่นๆ" : "Others",
        eventCount: otherItems.reduce(
          (acc, curr) => acc + (curr.eventCount ?? 0),
          0,
        ),
        fill: chartColors[formatData.length % chartColors.length],
      });
    }
    setChartData(formatData);
  }, [data]);

  const getChartHeight = () => {
    if (isSmallerThanMobileL) {
      return 250;
    } else if (isSmallerThanTabletL) {
      return 300;
    } else if (isSmallerThanDesktop && !isSmallerThanLaptop) {
      return 250;
    } else {
      return 250;
    }
  };

  const chartHeight = getChartHeight();

  return (
    <LabelSection
      title={getLocale() === "th" ? "หมวดหมู่ยอดนิยม" : "Popular Categories"}
      description={
        <Typography
          variant="body2"
          sx={{ ml: 2.5, "&.MuiTypography-root": { fontSize: font.size.xs } }}
        >
          {getLocale() === "th"
            ? `สัดส่วนยอดวิวแยกตามหมวด ${selectedTimeRangeData?.label?.th}`
            : `Proportion of views by category ${selectedTimeRangeData?.label?.en}`}
        </Typography>
      }
      style={{ marginBottom: 0, height: "100%" }}
      titleStyle={{ fontSize: font.size.lg, fontWeight: 600 }}
      headerStyle={{ marginBottom: 16 }}
    >
      <Grid container spacing={1.5} sx={{ flex: 1 }}>
        {!!data && data?.length > 0 ? (
          <>
            <Grid item xs={12}>
              <ResponsiveContainer width={"100%"} height={chartHeight}>
                <PieChart>
                  <Pie
                    activeShape={renderActiveShape}
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius="50%"
                    outerRadius="85%"
                    dataKey="eventCount"
                    paddingAngle={2}
                    isAnimationActive={isAnimationActive}
                  />
                  <Tooltip content={() => null} defaultIndex={defaultIndex} />
                </PieChart>
              </ResponsiveContainer>
            </Grid>

            {chartData.map((item, index) => (
              <Grid item xs={12} key={index}>
                <CategoryItem>
                  <CategoryTitle fill={item.fill}>
                    <SpanTitle>
                      {item.itemCategory}

                      <SpanDesc>
                        {`${(((item.eventCount ?? 0) * 100) / chartData.reduce((acc, curr) => acc + (curr.eventCount ?? 0), 0)).toFixed(2)}% ของยอดวิวรวม`}
                      </SpanDesc>
                    </SpanTitle>
                  </CategoryTitle>

                  <CategoryValue>
                    {convertIntToCurrency(item.eventCount ?? 0, false)}
                  </CategoryValue>
                </CategoryItem>

                {index < chartData.length - 1 && (
                  <Divider sx={{ borderColor: "#D8D8DC", mt: 1.5 }} />
                )}
              </Grid>
            ))}
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  "&.MuiTypography-root": {
                    fontSize: font.size.sm,
                    color: DarkGrey,
                  },
                }}
              >
                {getLocale() === "th" ? "ไม่มีข้อมูล" : "No data available"}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </LabelSection>
  );
};

export default PopularCategoryChart;
